import { React, useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import urls, { getCsrfToken } from '../com.zedock.common.script/urls.js';
import { clicks } from '../com.zedock.common.script/common.js';

import '../com.zedock.common.style/Policies.css';
import '../com.zedock.common.style/common.css';

function Policies() {
  const navigate = useNavigate();
  const location = useLocation();
  const { type } = useParams();

  useEffect(() => {
    clicks(location.pathname);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="container px-0    ">

            <div className="zd-wrap p-2 p-lg-4">

        <div className="row g-0">
          <nav aria-label="breadcrumb" className="zd-crumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <a href="" onClick={(e) => { e.preventDefault(); navigate("/Product") }}>Home</a>
              </li>
              <li className="breadcrumb-item active" aria-current="page">Policies</li>
            </ol>
          </nav>
        </div>

        <div className="row  g-2 mb-2">

          {/* ---------------- sidebar switcher ---------------- */}
          <div className="col-12 col-lg-3">
            <div className="zd-policy-nav">
              <span className="zd-policy-nav-label">Policies</span>
              <button className={`zd-policy-nav-link ${type === "PrivacyPolicy" ? "active" : ""}`} onClick={() => navigate("/Policies/PrivacyPolicy")}>Privacy Policy</button>
              <button className={`zd-policy-nav-link ${type === "TermsConditions" ? "active" : ""}`} onClick={() => navigate("/Policies/TermsConditions")}>Terms &amp; Conditions</button>
              <button className={`zd-policy-nav-link ${type === "ReturnRefundCancellationPolicy" ? "active" : ""}`} onClick={() => navigate("/Policies/ReturnRefundCancellationPolicy")}>Returns, Refunds &amp; Cancellation</button>
              <button className={`zd-policy-nav-link ${type === "ShippingPolicy" ? "active" : ""}`} onClick={() => navigate("/Policies/ShippingPolicy")}>Shipping Policy</button>
            </div>
          </div>

          {/* ---------------- article ---------------- */}
          <div className="col-12 col-lg-9">
            <div className="zd-policy-article">

              {type === "PrivacyPolicy" && (
                <div>
                  <span className="zd-policy-eyebrow">Legal</span>
                  <h1>Privacy Policy</h1>
                  <p className="zd-policy-effective">Effective Date: August 9, 2026</p>
                  <p className="zd-policy-intro">Zedock Solutions ("we", "our", "us") values your trust and is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit <strong>zedock.com</strong> and use our e-commerce services.</p>

                  <div className="zd-policy-section">
                    <h2>Information We Collect</h2>
                    <p>We may collect personal information such as your name, email address, phone number, billing and shipping address, and payment details when you register, place an order, or contact support.</p>
                    <p>We also collect non-personal information including browser type, device information, IP address, and cookies to improve site performance.</p>
                  </div>

                  <div className="zd-policy-section">
                    <h2>How We Use Your Information</h2>
                    <p>Your information is used to:</p>
                    <ul className="zd-policy-list">
                      {["Process and deliver orders", "Provide customer support", "Improve our website and services", "Send promotional offers (with your consent)", "Comply with legal obligations under Indian law"].map((t) => (
                        <li key={t}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Cookies &amp; Tracking</h2>
                    <p>We use cookies to personalize your shopping experience, remember preferences, and analyze site traffic. You may disable cookies in your browser settings, but some features may not function properly.</p>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Third-Party Services</h2>
                    <p>We may share limited information with trusted third-party partners such as payment gateways, logistics providers, and analytics services. These partners are required to protect your data in accordance with applicable laws.</p>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Your Rights</h2>
                    <p>Under Indian law, you have the right to access, correct, or delete your personal information. You may also withdraw consent for marketing communications at any time.</p>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Data Security</h2>
                    <p>We implement industry-standard security measures to protect your data against unauthorized access, alteration, or disclosure. However, no method of transmission over the Internet is 100% secure.</p>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Contact Us</h2>
                    <p>If you have any questions about this Privacy Policy, please contact us:</p>
                    <div className="zd-policy-contact">
                      <p>Email: <a href="mailto:support@zedock.com">support@zedock.com</a></p>
                      <p>Phone: <a href="tel:76193 11073">+91 76193 11073</a></p>
                      <p className="mb-0">Address: Zedock Solutions, Bengaluru, Karnataka, India</p>
                    </div>
                  </div>

                  <div className="zd-policy-section mb-0">
                    <h2>Policy Updates</h2>
                    <p className="mb-0">We may update this Privacy Policy from time to time. Changes will be posted on this page with the revised effective date.</p>
                  </div>
                </div>
              )}

              {type === "TermsConditions" && (
                <div>
                  <span className="zd-policy-eyebrow">Legal</span>
                  <h1>Terms &amp; Conditions</h1>
                  <p className="zd-policy-effective">Effective Date: August 9, 2026</p>
                  <p className="zd-policy-intro">Welcome to <strong>zedock.com</strong>. By accessing or using our e-commerce services, you agree to comply with and be bound by the following Terms &amp; Conditions. Please read them carefully.</p>

                  <div className="zd-policy-section">
                    <h2>User Agreement</h2>
                    <p>By registering, browsing, or purchasing from Zedock Solutions, you confirm that you are at least 18 years old and legally capable of entering into binding contracts under Indian law.</p>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Product Information</h2>
                    <p>We strive to provide accurate product descriptions, images, and pricing. However, errors may occur. Zedock Solutions reserves the right to correct inaccuracies and update information without prior notice.</p>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Payments</h2>
                    <p>All payments must be made through approved gateways. By placing an order, you authorize us to process your payment details securely. Prices are listed in INR and include applicable taxes unless stated otherwise.</p>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Shipping &amp; Delivery</h2>
                    <p>Orders are processed and shipped according to our <a href="" onClick={(e) => { e.preventDefault(); navigate("/Policies/ShippingPolicy") }}>Shipping Policy</a>. Delivery timelines may vary based on location and logistics partners.</p>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Returns &amp; Refunds</h2>
                    <p>Our <a href="" onClick={(e) => { e.preventDefault(); navigate("/Policies/ReturnRefundCancellationPolicy") }}>Return &amp; Refund Policy</a> governs all returns, cancellations, and refunds. Customers are encouraged to review this policy before making a purchase.</p>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Limitation of Liability</h2>
                    <p>Zedock Solutions shall not be held liable for indirect, incidental, or consequential damages arising from the use of our services, except as required under applicable Indian consumer protection laws.</p>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Governing Law</h2>
                    <p>These Terms &amp; Conditions are governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Bengaluru, Karnataka.</p>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Changes to Terms</h2>
                    <p>We may update these Terms &amp; Conditions from time to time. Updates will be posted on this page with the revised effective date.</p>
                  </div>

                  <div className="zd-policy-section mb-0">
                    <h2>Contact Us</h2>
                    <p>If you have any questions regarding these Terms &amp; Conditions, please contact us:</p>
                    <div className="zd-policy-contact">
                      <p>Email: <a href="mailto:support@zedock.com">support@zedock.com</a></p>
                      <p>Phone: <a href="tel:76193 11073">+91 76193 11073</a></p>
                      <p className="mb-0">Address: Zedock Solutions, Bengaluru, Karnataka, India</p>
                    </div>
                  </div>
                </div>
              )}

              {type === "ReturnRefundCancellationPolicy" && (
                <div>
                  <span className="zd-policy-eyebrow">Legal</span>
                  <h1>Return, Refund &amp; Cancellation Policy</h1>
                  <p className="zd-policy-intro">At <strong>Zedock Solutions</strong> (<a href="https://zedock.com">zedock.com</a>), we value your satisfaction and strive to provide a smooth shopping experience. Please read our policy carefully to understand your rights and obligations.</p>

                  <div className="zd-policy-section">
                    <h2>Returns</h2>
                    <p>You may request a return within <strong>7 days</strong> of receiving your order if the product is defective, damaged, or not as described. Items must be unused, in original packaging, and accompanied by proof of purchase.</p>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Refunds</h2>
                    <ul className="zd-policy-list">
                      <li>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                        Approved returns will be refunded to your original payment method within <strong>&nbsp;7–10 business days</strong>.
                      </li>
                      <li>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                        Shipping charges are non-refundable unless the return is due to our error.
                      </li>
                      <li>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                        Partial refunds may be issued if the product shows signs of use or damage not caused during delivery.
                      </li>
                    </ul>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Cancellations</h2>
                    <p>Orders can be cancelled before they are shipped. Once shipped, cancellation requests will be treated as a return.</p>
                  </div>

                  <div className="zd-policy-section">
                    <h2>Exceptions</h2>
                    <ul className="zd-policy-list">
                      <li>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        Products marked "Final Sale" or "Non-returnable" cannot be returned or refunded.
                      </li>
                      <li>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        Perishable goods, personal care items, and customized products are not eligible for return.
                      </li>
                    </ul>
                  </div>

                  <div className="zd-policy-section mb-0">
                    <h2>Contact Us</h2>
                    <p>If you have any questions or wish to initiate a return, refund, or cancellation, please reach out to our support team:</p>
                    <div className="zd-policy-contact">
                      <p>Email: <a href="mailto:support@zedock.com">support@zedock.com</a></p>
                      <p className="mb-0">Phone: <a href="tel:76193 11073">+91 76193 11073</a></p>
                    </div>
                  </div>
                </div>
              )}

              {type === "ShippingPolicy" && (
                <div>
                  <span className="zd-policy-eyebrow">Legal</span>
                  <h1>Shipping Policy</h1>
                  <p className="zd-policy-intro">Welcome to Zedock Solutions (zedock.com). This Shipping Policy explains how we process, deliver, and manage orders in compliance with Indian e-commerce regulations.</p>

                  <div className="accordion zd-policy-accordion" id="shippingPolicyAccordion">
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingOne">
                        <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne">Order Processing</button>
                      </h2>
                      <div id="collapseOne" className="accordion-collapse collapse show" data-bs-parent="#shippingPolicyAccordion">
                        <div className="accordion-body">Orders are processed within 1–2 business days after payment confirmation. You will receive an email and SMS notification once your order is confirmed.</div>
                      </div>
                    </div>

                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingTwo">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo">Delivery Timelines</button>
                      </h2>
                      <div id="collapseTwo" className="accordion-collapse collapse" data-bs-parent="#shippingPolicyAccordion">
                        <div className="accordion-body">Standard delivery within India takes 3–7 business days depending on your location. For Bengaluru jurisdiction, delivery is typically completed within 2–4 business days.</div>
                      </div>
                    </div>

                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingThree">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree">Shipping Charges</button>
                      </h2>
                      <div id="collapseThree" className="accordion-collapse collapse" data-bs-parent="#shippingPolicyAccordion">
                        <div className="accordion-body">Shipping charges are calculated at checkout based on product weight, dimensions, and delivery location. Free shipping may be offered on promotional orders.</div>
                      </div>
                    </div>

                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingFour">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFour">Tracking &amp; Failed Delivery</button>
                      </h2>
                      <div id="collapseFour" className="accordion-collapse collapse" data-bs-parent="#shippingPolicyAccordion">
                        <div className="accordion-body">Once shipped, you will receive a tracking ID via email/SMS. In case of failed delivery attempts, our courier partner will re-attempt delivery or contact you directly.</div>
                      </div>
                    </div>

                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingFive">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFive">Damaged or Lost Packages</button>
                      </h2>
                      <div id="collapseFive" className="accordion-collapse collapse" data-bs-parent="#shippingPolicyAccordion">
                        <div className="accordion-body">If your package is damaged or lost in transit, please contact our support team within 48 hours of delivery attempt. We will initiate a replacement or refund as per our Return &amp; Refund Policy.</div>
                      </div>
                    </div>
                  </div>

                  <div className="zd-policy-note">
                    <strong>Note:</strong> All shipping practices comply with the Consumer Protection (E-Commerce) Rules, 2020 and Legal Metrology (Packaged Commodities) Rules, 2011 under Indian law.
                  </div>
                </div>
              )}

            </div>
          </div>

        </div>

      </div>

    </div>
  );
}

export default Policies;
